import {Chess} from "chess.js";

// Turns Rook's answer into pieces the chat can render: plain text, bold text, clickable squares, and move
// sequences written as [[Nxe5 Bxd1 Bxf7+]]. Every sequence is checked with chess.js from the position the question
// was asked in. Illegal moves are cut off, and a sequence without any legal move is shown as plain text.

const flipTurn = (fen) => {
    const parts = fen.split(" ");
    parts[1] = parts[1] === "w" ? "b" : "w";
    parts[3] = "-";
    return parts.join(" ");
};

const cleanToken = (token) => token.replace(/^\d+\.+/, "").replace(/[!?]+$/, "").trim();

const playFrom = (fen, tokens) => {
    let chess;
    try {
        chess = new Chess(fen);
    } catch (e) {
        return [];
    }
    const moves = [];
    for (const token of tokens) {
        try {
            const m = chess.move(token);
            moves.push({san: m.san, from: m.from, to: m.to, fen: chess.fen()});
        } catch (e) {
            break;
        }
    }
    return moves;
};

// "5. Nxe5 Bxd1 6. Bxf7+"
const labelFor = (startFen, moves) => {
    const parts = startFen.split(" ");
    let turn = parts[1];
    let number = Number(parts[5]) || 1;
    return moves.map((m, i) => {
        let text = m.san;
        if (turn === "w") text = `${number}. ${m.san}`;
        else if (i === 0) text = `${number}... ${m.san}`;
        if (turn === "b") number += 1;
        turn = turn === "w" ? "b" : "w";
        return text;
    }).join(" ");
};

export const parseLine = (content, fen) => {
    const tokens = content.split(/\s+/).map(cleanToken).filter((t) => t && !/^\.+$/.test(t) && !/^\d+$/.test(t));
    if (!tokens.length) return null;
    let startFen = fen;
    let moves = playFrom(fen, tokens);
    let threat = false;
    if (!moves.length) {
        // Maybe it is the opponent's idea (what they threaten), which starts with their move
        try {
            if (!new Chess(fen).inCheck()) {
                const flipped = flipTurn(fen);
                const tried = playFrom(flipped, tokens);
                if (tried.length) {
                    startFen = flipped;
                    moves = tried;
                    threat = true;
                }
            }
        } catch (e) {
            // not a valid position, so no threat line either
        }
    }
    if (!moves.length) return null;
    return {startFen, moves, threat, label: labelFor(startFen, moves)};
};

const SAN_PIECE = "(?:O-O-O|O-O|[KQRBN][a-h]?[1-8]?x?[a-h][1-8]|[a-h]x[a-h][1-8](?:=[QRBN])?)[+#]?";
const TOKEN = new RegExp(`(\\*\\*)|(?<![\\w])(${SAN_PIECE})(?![\\w])|(?<![\\w-])([a-h][1-8])(?![\\w])`, "g");

// Splits one paragraph's plain text (no [[ ]]) into text, bold, move and square pieces
const splitText = (text, fen, state, out) => {
    let last = 0;
    const push = (piece) => out.push({...piece, bold: state.bold});
    for (const match of text.matchAll(TOKEN)) {
        if (match.index > last) push({type: "text", text: text.slice(last, match.index)});
        last = match.index + match[0].length;
        if (match[1]) {
            state.bold = !state.bold;
        } else if (match[2]) {
            const line = parseLine(match[2], fen);
            if (line && line.moves.length === 1) push({type: "line", line, inline: true});
            else push({type: "text", text: match[2]});
        } else if (match[3]) {
            push({type: "square", square: match[3]});
        }
    }
    if (last < text.length) push({type: "text", text: text.slice(last)});
};

/**
 * Returns paragraphs, each a list of pieces: {type: "text"|"square"|"line", bold, ...}.
 * While an answer is still streaming, an unfinished [[ at the end is hidden until it is complete.
 */
export const parseRookText = (text, fen, streaming = false) => {
    let source = text || "";
    if (streaming) {
        const open = source.lastIndexOf("[[");
        if (open >= 0 && source.indexOf("]]", open) < 0) source = source.slice(0, open);
    }
    return source.split(/\n{2,}/).map((paragraph) => {
        const state = {bold: false};
        const pieces = [];
        let last = 0;
        const lineRe = /\[\[([^\]]*)\]\]/g;
        for (const match of paragraph.matchAll(lineRe)) {
            if (match.index > last) splitText(paragraph.slice(last, match.index), fen, state, pieces);
            last = match.index + match[0].length;
            const line = parseLine(match[1], fen);
            if (line) pieces.push({type: "line", line, bold: state.bold});
            else if (match[1].trim()) pieces.push({type: "text", text: match[1].trim(), bold: state.bold});
        }
        if (last < paragraph.length) splitText(paragraph.slice(last), fen, state, pieces);
        return pieces;
    }).filter((pieces) => pieces.some((p) => p.type !== "text" || p.text.trim()));
};
